/**
 * Sidecar client registry
 *
 * Shared browser-side storage for Sidecar chat status state.
 *
 * Primary responsibilities:
 * - Keep a per-chat Sidecar status registry in localStorage
 * - Emit same-tab change events after localStorage writes
 *
 * Dependencies:
 * - types/sidecar
 */

'use client'

import type {
  SidecarChatStatus,
  SidecarRunEventData,
  SidecarRunState
} from '@/types/sidecar'

export const SIDECAR_CHAT_REGISTRY_STORAGE_KEY = 'sidecar.chat.registry'
export const SIDECAR_REGISTRY_EVENT = 'sidecar-registry-changed'

type StoredSidecarRunRegistry = Record<string, SidecarChatStatus>

function canUseBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function dispatchRegistryChange() {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(SIDECAR_REGISTRY_EVENT))
}

function readJsonFromStorage<T>(storageKey: string, fallback: T): T {
  if (!canUseBrowserStorage()) return fallback

  try {
    const rawValue = window.localStorage.getItem(storageKey)
    if (!rawValue) return fallback

    return JSON.parse(rawValue) as T
  } catch {
    return fallback
  }
}

function writeJsonToStorage(storageKey: string, value: unknown) {
  if (!canUseBrowserStorage()) return

  window.localStorage.setItem(storageKey, JSON.stringify(value))
}

function isSidecarStatus(value: unknown): value is SidecarChatStatus {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false

  const record = value as Record<string, unknown>

  return (
    typeof record.chatId === 'string' &&
    typeof record.runId === 'string' &&
    typeof record.status === 'string' &&
    typeof record.updatedAt === 'number' &&
    typeof record.unread === 'boolean'
  )
}

function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'error' || status === 'cancelled'
}

function getSidecarRunRegistryKey(chatId: string, runId: string): string {
  return `${chatId}:${runId}`
}

export function readSidecarRunRegistry(): Record<string, SidecarChatStatus> {
  const registry = readJsonFromStorage<StoredSidecarRunRegistry>(
    SIDECAR_CHAT_REGISTRY_STORAGE_KEY,
    {}
  )

  return Object.fromEntries(
    Object.entries(registry).filter(([, value]) => isSidecarStatus(value))
  )
}

export function getLatestSidecarChatStatuses(
  registry: Record<string, SidecarChatStatus>
): Record<string, SidecarChatStatus> {
  const statuses = Object.values(registry).sort((left, right) => {
    if (left.updatedAt !== right.updatedAt) {
      return left.updatedAt - right.updatedAt
    }

    if ((left.completedAt ?? 0) !== (right.completedAt ?? 0)) {
      return (left.completedAt ?? 0) - (right.completedAt ?? 0)
    }

    return left.runId.localeCompare(right.runId)
  })

  const latestByChat: Record<string, SidecarChatStatus> = {}

  for (const status of statuses) {
    latestByChat[status.chatId] = mergeSidecarChatStatus(
      latestByChat[status.chatId],
      status
    )
  }

  return latestByChat
}

export function readSidecarChatRegistry(): Record<string, SidecarChatStatus> {
  return getLatestSidecarChatStatuses(readSidecarRunRegistry())
}

export function writeSidecarChatRegistry(
  registry: Record<string, SidecarChatStatus>
) {
  writeJsonToStorage(SIDECAR_CHAT_REGISTRY_STORAGE_KEY, registry)
  dispatchRegistryChange()
}

export function createSidecarChatStatus(
  run:
    | Pick<
        SidecarRunState,
        'chatId' | 'runId' | 'status' | 'objective' | 'message' | 'completedAt'
      >
    | Pick<
        SidecarRunEventData,
        'chatId' | 'runId' | 'status' | 'objective' | 'message' | 'completedAt'
      >,
  options?: {
    unread?: boolean
    now?: number
  }
): SidecarChatStatus {
  const status =
    run.status === 'error'
      ? 'error'
      : run.status === 'cancelled'
        ? 'cancelled'
        : run.status === 'completed' || run.completedAt
          ? 'completed'
          : run.status

  return {
    chatId: run.chatId,
    runId: run.runId,
    status,
    objective: run.objective,
    message: run.message,
    updatedAt: options?.now ?? Date.now(),
    completedAt: run.completedAt,
    unread: options?.unread ?? false
  }
}

export function mergeSidecarChatStatus(
  current: SidecarChatStatus | undefined,
  incoming: SidecarChatStatus
): SidecarChatStatus {
  if (!current) return incoming

  const currentIsTerminal = isTerminalStatus(current.status)
  const incomingIsTerminal = isTerminalStatus(incoming.status)
  const shouldKeepCurrent =
    current.runId === incoming.runId &&
    currentIsTerminal &&
    !incomingIsTerminal

  if (shouldKeepCurrent) {
    return current
  }

  if (current.updatedAt > incoming.updatedAt && current.runId !== incoming.runId) {
    return current
  }

  return {
    ...current,
    ...incoming,
    status:
      current.runId === incoming.runId && currentIsTerminal && incomingIsTerminal
        ? current.completedAt && incoming.completedAt
          ? current.completedAt > incoming.completedAt
            ? current.status
            : incoming.status
          : incoming.status
        : incoming.status,
    unread: incoming.unread ?? current.unread,
    updatedAt: Math.max(current.updatedAt, incoming.updatedAt),
    completedAt:
      current.completedAt && incoming.completedAt
        ? Math.max(current.completedAt, incoming.completedAt)
        : incoming.completedAt ?? current.completedAt
  }
}

export function upsertSidecarChatStatus(
  registry: Record<string, SidecarChatStatus>,
  incoming: SidecarChatStatus
): Record<string, SidecarChatStatus> {
  const registryKey = getSidecarRunRegistryKey(incoming.chatId, incoming.runId)

  return {
    ...registry,
    [registryKey]: mergeSidecarChatStatus(registry[registryKey], incoming)
  }
}

export function markSidecarChatSeen(
  chatId: string,
  options?: { runId?: string }
): Record<string, SidecarChatStatus> {
  const registry = readSidecarRunRegistry()
  const latestByChat = getLatestSidecarChatStatuses(registry)
  const current =
    options?.runId
      ? registry[getSidecarRunRegistryKey(chatId, options.runId)]
      : latestByChat[chatId]

  if (!current) return latestByChat
  if (options?.runId && current.runId !== options.runId) return latestByChat
  if (!current.unread) return latestByChat

  let didChange = false
  const nextRegistry: Record<string, SidecarChatStatus> = {}

  for (const [registryKey, status] of Object.entries(registry)) {
    if (status.chatId !== chatId || !status.unread) {
      nextRegistry[registryKey] = status
      continue
    }

    didChange = true
    nextRegistry[registryKey] = {
      ...status,
      unread: false
    }
  }

  if (!didChange) return latestByChat

  writeSidecarChatRegistry(nextRegistry)

  return getLatestSidecarChatStatuses(nextRegistry)
}
